import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { NavigationService } from '@treasury/core/navigation';
import { EntitlementsService } from '@treasury/domain/entitlements';
import { WiresService } from '@treasury/domain/wires';
import { TmContainer } from '@treasury/presentation';
import { dollarIcon, layerGroupIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-footer';
import { TmDialogService } from '@treasury/presentation/services/dialog';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '../../components/confirmation-container';
import { ValidWireDateTime } from '../types/wire-valid-datetime.type';

export const tagName = 'wire-select-step';
@customElement(tagName)
export class WireSelectStep extends TmContainer {
    @InjectProperty()
    declare private wireService: WiresService;

    @InjectProperty()
    declare private readonly navService: NavigationService;

    @InjectProperty()
    declare private readonly dialogService: TmDialogService;

    @InjectProperty()
    declare private readonly entitlementsService: EntitlementsService;

    @state()
    private domesticEntitlement = false;

    @state()
    private internationalEntitlement = false;

    @state()
    private validWireDateTime: ValidWireDateTime | undefined;

    @state()
    private internationalDisabled = false;

    @state()
    private dialog = {
        content: '',
        title: '',
    };

    protected async firstUpdated() {
        await this.tryFetch(
            async () =>
                Promise.all([
                    this.entitlementsService.getEntitlements(),
                    this.wireService.checkValidDateTime(),
                ]),
            ([entitlements, validWireDateTime]) => {
                this.domesticEntitlement = entitlements.get('Wire, Domestic, Create Wire') || false;
                this.internationalEntitlement =
                    entitlements.get('Wire, International, Create Wire') || false;
                this.validWireDateTime = validWireDateTime;

                if (this.validWireDateTime.isAfterCutoff) {
                    this.internationalDisabled = true;
                    this.dialog.title = 'Cutoff Time Passed';
                    this.dialog.content =
                        'International wires may not be submitted after the processing cutoff time.';
                }

                if (this.validWireDateTime.isHoliday || this.validWireDateTime.isWeekend) {
                    this.internationalDisabled = true;
                    this.dialog.title = 'International Wires Unavailable';
                    this.dialog.content =
                        'International wires are not available on weekends or holidays.';
                }
            }
        );
    }

    protected render() {
        return [
            html`<div class="px-4 py-6 font-medium text-sm text-center bg-primary">
                What type of wire payment would you like to make?
            </div>`,
            this.renderDomesticAction(),
            this.renderInternationalAction(),
            this.renderTemplateAction(),
        ];
    }

    private renderDomesticAction() {
        if (!this.domesticEntitlement) {
            return nothing;
        }

        return html`<tm-action-button
            class="mt-[8px] mb-[8px]"
            .disabled=${false}
            .buttonTitle=${'Domestic USD wire'}
            @click=${() => {
                this.navService.navigate(`/wires/domestic/create/new/usd`);
            }}
        ></tm-action-button>`;
    }

    private renderInternationalAction() {
        if (!this.internationalEntitlement) {
            return nothing;
        }
        return html` <tm-action-button
            class="mt-[8px] mb-[8px]"
            .disabled=${false}
            .icon=${dollarIcon}
            .buttonTitle=${'International USD wire'}
            @click=${() => {
                if (this.internationalDisabled) {
                    this.triggerDialog();
                } else {
                    this.navService.navigate(`/wires/international/create/new/usd`);
                }
            }}
        ></tm-action-button>`;
    }

    private triggerDialog() {
        return this.dialogService.open(this.dialog.content, this.dialog.title, {
            buttons: [
                {
                    label: 'Ok',
                    reason: DynamicComponentExitReason.Confirm,
                },
            ],
            classes: ['max-w-[700px]'],
        });
    }

    private renderTemplateAction() {
        if (this.domesticEntitlement || this.internationalEntitlement) {
            return html`<tm-action-button
                class="mt-[8px] mb-[8px]"
                .icon=${layerGroupIcon}
                .buttonTitle=${'Create from template'}
                @click=${() => {
                    this.navService.navigate(`/wires/templates/initiate`);
                }}
            ></tm-action-button>`;
        }
        return nothing;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WireSelectStep;
    }
}
